// ============================================================
// UsersPage — liste, recherche, disable/enable
// [2.13.0] « Afficher plus » : au-delà des 50 premiers comptes (avant, les
// plus anciens n'étaient trouvables que par la recherche). On ne peut plus
// désactiver son propre compte depuis la console.
// ============================================================

import { useEffect, useState } from 'react';
import { api } from '../api';
import type { User } from '@siverix/shared';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../auth/AuthContext';

const PAGE_SIZE = 50;

export function UsersPage(): JSX.Element {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { socket } = useSocket();

  async function fetchList(query?: string): Promise<void> {
    setLoading(true);
    setErr(null);
    try {
      const list = await api.adminListUsers(query, { limit: PAGE_SIZE });
      setUsers(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchMore(): Promise<void> {
    const last = users[users.length - 1];
    if (!last) return;
    setLoadingMore(true);
    setErr(null);
    try {
      const next = await api.adminListUsers(q || undefined, { cursor: last.id, limit: PAGE_SIZE });
      setUsers((prev) => [...prev, ...next.filter((u) => !prev.some((p) => p.id === u.id))]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void fetchList();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const refresh = (): void => void fetchList(q || undefined);
    socket.on('user:created', refresh);
    socket.on('user:updated', refresh);
    socket.on('user:disabled', refresh);
    return () => {
      socket.off('user:created', refresh);
      socket.off('user:updated', refresh);
      socket.off('user:disabled', refresh);
    };
  }, [socket, q]);

  async function toggle(u: User): Promise<void> {
    if (
      !u.isDisabled &&
      !window.confirm(
        `Désactiver le compte ${u.username} ? Ses sessions sont fermées et il ne peut plus se connecter.`,
      )
    ) {
      return;
    }
    setBusyId(u.id);
    try {
      if (u.isDisabled) await api.adminEnableUser(u.id);
      else await api.adminDisableUser(u.id);
      await fetchList(q || undefined);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Utilisateurs</h1>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <input
          type="search"
          placeholder="Email, pseudo, displayName…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void fetchList(q || undefined)}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: '6px',
          }}
        />
        <button onClick={() => void fetchList(q || undefined)} style={btnStyle}>
          Rechercher
        </button>
      </div>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: 'var(--color-bg)' }}>
                <th style={th}>Pseudo</th>
                <th style={th}>Email</th>
                <th style={th}>Rôle</th>
                <th style={th}>État</th>
                <th style={th}>Créé</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={td}>
                    <strong>{u.username}</strong>
                    {u.displayName && (
                      <span style={{ color: 'var(--color-text-muted)', marginLeft: 6 }}>
                        ({u.displayName})
                      </span>
                    )}
                  </td>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.isAdmin ? <Badge label="ADMIN" tone="accent" /> : '—'}</td>
                  <td style={td}>
                    {u.isDisabled ? (
                      <Badge label="DÉSACTIVÉ" tone="error" />
                    ) : (
                      <Badge label="ACTIF" tone="success" />
                    )}
                  </td>
                  <td style={td}>{new Date(u.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {u.id === me?.id ? (
                      <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                        Ton compte
                      </span>
                    ) : (
                      <button
                        disabled={busyId === u.id}
                        onClick={() => void toggle(u)}
                        style={smallBtnStyle}
                      >
                        {busyId === u.id ? '…' : u.isDisabled ? 'Réactiver' : 'Désactiver'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      ...td,
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      padding: '24px',
                    }}
                  >
                    Aucun utilisateur.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {hasMore && (
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <button onClick={() => void fetchMore()} disabled={loadingMore} style={smallBtnStyle}>
                {loadingMore ? '…' : 'Afficher plus'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: 'accent' | 'success' | 'error' | 'muted';
}): JSX.Element {
  const map: Record<string, string> = {
    // Fond translucide tiré de l'accent du thème (et non un bleu fixe).
    accent: 'color-mix(in srgb, var(--color-accent) 18%, transparent)',
    success: 'rgba(63,185,80,.18)',
    error: 'rgba(248,81,73,.18)',
    muted: 'rgba(139,148,158,.18)',
  };
  const colorMap: Record<string, string> = {
    accent: 'var(--color-accent)',
    success: 'var(--color-success)',
    error: 'var(--color-error)',
    muted: 'var(--color-text-muted)',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        background: map[tone],
        color: colorMap[tone],
        fontSize: '11px',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '10px 16px' };
const btnStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  cursor: 'pointer',
};
const smallBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
};

export { Badge };
